import { ClearOutlined, TableOutlined, AppstoreOutlined } from "@ant-design/icons";
import { Button, Select, Segmented, Radio, Flex } from "antd";
import Search from "antd/es/input/Search";
import React, { useEffect, useRef, useState } from "react";
import { JobPostsFilters } from "../../Interface";
import { JobDescriptionEntity } from "../../Entities/JobDescription";
import "./JobPostsFilterMenu.scss";

interface JobPostsFilterMenuProps {
  updateFilters: (key: keyof JobPostsFilters, value: string | null) => void;
  filters: JobPostsFilters;
  clearAllFilters: () => void;
  handleSortFilter: (
    sort: keyof JobDescriptionEntity | null,
    order: "asc" | "desc" | null
  ) => void;
  viewMode?: "table" | "card";
  onViewModeChange?: (mode: "table" | "card") => void;
}

const JobPostsFilterMenu: React.FC<JobPostsFilterMenuProps> = ({
  updateFilters,
  filters,
  clearAllFilters,
  handleSortFilter,
  viewMode = "table",
  onViewModeChange,
}) => {
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const handleSorting = (value: string | undefined) => {
    if (value === undefined) {
      handleSortFilter(null, null);
    } else {
      switch (value) {
        case "newest_first":
          handleSortFilter("createdAt", "desc");
          break;
        case "oldest_first":
          handleSortFilter("createdAt", "asc");
          break;
        case "recently_updated":
          handleSortFilter("updatedAt", "desc");
          break;
      }
    }
  };

  const handleStatusFilter = (value: boolean | undefined) => {
    let status: string | null = null;
    if (value !== undefined) {
      status = value ? "active" : "inactive";
    }
    updateFilters("status", status);
  };

  const handleSearch = (value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    updateFilters("search", value || null);
  };

  const getSatatusValue = () => {
    if (filters.status === "active") {
      return true;
    } else if (filters.status === "inactive") {
      return false;
    } else {
      return undefined;
    }
  };

  const getSortValue = () => {
    if (filters.sort === "createdAt" && filters.order === "desc") {
      return "newest_first";
    } else if (filters.sort === "createdAt" && filters.order === "asc") {
      return "oldest_first";
    } else if (filters.sort === "updatedAt" && filters.order === "desc") {
      return "recently_updated";
    } else {
      return undefined;
    }
  };

  const handleClearAll = () => {
    clearAllFilters();
    setSearchText(undefined);
  };

  useEffect(() => {
    setSearchText(filters.search || "");
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchText !== (filters.search || "")) {
      debounceRef.current = setTimeout(() => {
        updateFilters("search", searchText || null);
      }, 500);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchText, filters.search]);

  return (
    <section className="job-posts-filter-menu">
      <Flex align="center" justify="flex-end" gap={8}>
        <Search
          className="filter-search"
          allowClear
          placeholder="search job posts"
          size="middle"
          onSearch={handleSearch}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          enterButton
        />

        <Select
          size="middle"
          allowClear
          placeholder="Filter by status"
          options={[
            { value: true, label: "Active" },
            { value: false, label: "Inactive" },
          ]}
          onChange={handleStatusFilter}
          value={getSatatusValue()}
        />

        <Select
          size="middle"
          allowClear
          placeholder="Sort by"
          options={[
            { value: "newest_first", label: "Latest first" },
            { value: "oldest_first", label: "Oldest first" },
            { value: "recently_updated", label: "Recently updated" },
          ]}
          onChange={handleSorting}
          value={getSortValue()}
        />

        {onViewModeChange && (
          <Radio.Group
            className="view-mode-group"
            size="middle"
            optionType="button"
            buttonStyle="solid"
            value={viewMode}
            onChange={(e) => onViewModeChange(e.target.value as "table" | "card")}
          >
            <Radio.Button value="table">
              <TableOutlined />
            </Radio.Button>
            <Radio.Button value="card">
              <AppstoreOutlined />
            </Radio.Button>
          </Radio.Group>
        )}

        <Button
          type="default"
          size="middle"
          icon={<ClearOutlined />}
          onClick={handleClearAll}
          disabled={Object.keys(filters).every(
            (key) => filters[key as keyof JobPostsFilters] === null
          )}
        >
          Clear All
        </Button>


      </Flex>
    </section>
  );
};

export default JobPostsFilterMenu;
